import type { HotelListRow } from "./types";
import type {
  ProvisioningStep,
  ProvisioningUser,
} from "../../onboarding-tracking/types";
import { normalizeCustomerId } from "./hooks/useOnboardingPipelines";

/**
 * One row of the "Still onboarding" section — the union of the two systems'
 * "not live yet" notions, joined on the Google Ads customer id:
 *
 *  - `user` set: a SEM-provisioning pipeline exists (keyed by platform
 *    userId). `hotel` is additionally set once that Ads account is enrolled
 *    in the tracking fleet.
 *  - `user` null: an enrolled, not-yet-live hotel with no pipeline recorded —
 *    onboarded before the pipeline shipped. Tracking is all we know.
 */
export interface OnboardingMergedRow {
  key: string;
  user: ProvisioningUser | null;
  hotel: HotelListRow | null;
}

/** Tracking Health's "still onboarding" notion: the not-yet-live gate. */
export function isNotYetLive(row: HotelListRow): boolean {
  return (
    row.status.gate === "not-yet-live" ||
    row.status.reported === "not-yet-live"
  );
}

/**
 * The step the pipeline is waiting on — the first non-terminal step in
 * display order (a mid-pipeline failure IS that step, since completed ones
 * are skipped over). Null when every step is COMPLETED/SKIPPED.
 */
export function currentPipelineStep(
  user: ProvisioningUser,
): { step: ProvisioningStep; total: number } | null {
  const step =
    user.steps.find(
      (s) =>
        s.status === "IN_PROGRESS" ||
        s.status === "AWAITING_MANUAL" ||
        s.status === "FAILED" ||
        s.status === "PENDING",
    ) ?? null;
  return step ? { step, total: user.steps.length } : null;
}

/** An unfinished pipeline whose hotel is already serving — bookkeeping debt,
 *  not onboarding. Kept visible (and hideable), sorted after everything that
 *  is genuinely still onboarding. */
export function isLiveJoined(row: OnboardingMergedRow): boolean {
  return Boolean(row.hotel && !isNotYetLive(row.hotel));
}

// Attention order for the section: broken first, actively running next, the
// waiting-on-a-human norm after, then the tail. Ties break newest-touched
// first.
const STATUS_RANK: Record<string, number> = {
  FAILED: 0,
  IN_PROGRESS: 1,
  AWAITING_MANUAL: 2,
  NOT_STARTED: 3,
  COMPLETED: 4,
};

function rowRank(row: OnboardingMergedRow): number {
  const base = !row.user
    ? 5 // tracking-only legacy rows after the pipeline rows
    : STATUS_RANK[row.user.overallStatus] ?? 3;
  return isLiveJoined(row) ? base + 10 : base;
}

function rowTouchedAt(row: OnboardingMergedRow): number {
  const iso = row.user?.lastUpdatedAt ?? row.user?.startedAt ?? null;
  return iso ? new Date(iso).getTime() : 0;
}

/**
 * Split the fleet + pipeline census into the two sections of the merged
 * table.
 *
 * A pipeline row is "still onboarding" while it is not COMPLETED — and a
 * COMPLETED one stays only while its hotel has not actually gone live
 * (enrolled but the campaigns have never served). A COMPLETED pipeline with
 * no enrolled hotel is dropped rather than shown: that shape is either a
 * <1-day enrollment lag (fleet sync will pick it up) or an account that has
 * since left the fleet — both would otherwise sit in the section forever.
 */
export function splitFleet(
  hotels: readonly HotelListRow[],
  pipelines: readonly ProvisioningUser[],
): { live: HotelListRow[]; onboarding: OnboardingMergedRow[] } {
  const hotelByCid = new Map<string, HotelListRow>();
  for (const hotel of hotels) {
    const cid = normalizeCustomerId(hotel.customerId);
    if (cid) hotelByCid.set(cid, hotel);
  }

  const live = hotels.filter((hotel) => !isNotYetLive(hotel));

  const onboarding: OnboardingMergedRow[] = [];
  const claimedCids = new Set<string>();

  for (const user of pipelines) {
    const cid = normalizeCustomerId(user.googleAdsCustomerId);
    const hotel = cid ? hotelByCid.get(cid) ?? null : null;
    if (user.overallStatus === "COMPLETED") {
      if (!hotel || !isNotYetLive(hotel)) continue; // graduated (or gone)
    }
    if (cid) claimedCids.add(cid);
    onboarding.push({ key: user.userId, user, hotel });
  }

  for (const hotel of hotels) {
    if (!isNotYetLive(hotel)) continue;
    const cid = normalizeCustomerId(hotel.customerId);
    if (cid && claimedCids.has(cid)) continue;
    onboarding.push({ key: hotel.customerId, user: null, hotel });
  }

  onboarding.sort((a, b) => {
    const rank = rowRank(a) - rowRank(b);
    if (rank !== 0) return rank;
    return rowTouchedAt(b) - rowTouchedAt(a);
  });

  return { live, onboarding };
}
